import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Check, Share2, Trash2 } from 'lucide-react';

import { ApiError } from '@/lib/api/errors';
import { queryKeys } from '@/lib/api/queryKeys';
import { posterDesignOf, xurveyApi, type PosterPreset } from '@/lib/api/xurvey';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PromptDialog } from '@/components/ui/PromptDialog';
import { Select } from '@/components/ui/Input';
import { ApplyStyleDialog } from './ApplyStyleDialog';
import { useT } from '@/lib/i18n/useT';

/**
 * Los diseños de cartel guardados de la empresa, arriba de la pestaña Cartel.
 *
 * Es `StylePresetBar` para el cartel, y existe por lo mismo: montar una fecha
 * empezaba por rehacer el fondo, los colores y el logo del QR de la anterior.
 * Se guarda una vez y se aplica en el siguiente evento.
 *
 * **Sólo el diseño.** Lo que se guarda y lo que se aplica son las claves de
 * `POSTER_DESIGN_KEYS`; el titular, la descripción y la llamada a la acción
 * son de cada evento y no se tocan. Por eso vive DENTRO de la pestaña Cartel y
 * no junto a los estilos: lo que cambia al aplicarlo es lo que se está mirando.
 */
export function PosterPresetBar({
  poster,
  seleccionado: seleccionadoGuardado = null,
  modificado = false,
  onAplicar,
}: {
  /** El cartel que se está viendo ahora, para poder guardar su diseño. */
  poster: Record<string, unknown>;
  /** El diseño del que partió ESTE despliegue (`overrides.posterPresetId`). */
  seleccionado?: number | null;
  /** Si el cartel se tocó después de aplicarlo. Se dice, no se esconde. */
  modificado?: boolean;
  onAplicar: (preset: PosterPreset) => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [nombrando, setNombrando] = useState(false);
  const [aRetirar, setARetirar] = useState<PosterPreset | null>(null);
  const [aplicandoA, setAplicandoA] = useState<PosterPreset | null>(null);
  const elegido = seleccionadoGuardado;

  const presets = useQuery({
    queryKey: queryKeys.xurvey.posterPresets,
    queryFn: () => xurveyApi.listPosterPresets(),
    staleTime: 60_000,
  });

  const guardar = useMutation({
    mutationFn: (name: string) =>
      xurveyApi.createPosterPreset({ name, poster: posterDesignOf(poster) }),
    onSuccess: (creado: PosterPreset) => {
      setError(null);
      setNombrando(false);
      // Se aplica el recién creado: así queda marcado en el despliegue y ver
      // tu diseño recién puesto es la confirmación de que se guardó.
      onAplicar(creado);
      void queryClient.invalidateQueries({ queryKey: queryKeys.xurvey.posterPresets });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('designJourney.posterPresetSaveFailed')),
  });

  const retirar = useMutation({
    mutationFn: (id: number) => xurveyApi.removePosterPreset(id),
    onSuccess: () => {
      setError(null);
      setARetirar(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.xurvey.posterPresets });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('designJourney.posterPresetDeleteFailed')),
  });

  const items = presets.data?.items ?? [];
  // Derivado: si el diseño elegido se retira, la selección se cae sola.
  const seleccionado = items.find((p) => p.id === elegido) ?? null;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border-default)] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Bookmark className="h-3.5 w-3.5 text-[var(--color-fg-muted)]" aria-hidden />
        <span className="flex-1 text-[11px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          {t('designJourney.posterPresets')}
        </span>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setNombrando(true);
          }}
          disabled={guardar.isPending}
          className="rounded-md border border-[var(--color-border-default)] px-2 py-1 text-[11px] hover:border-[var(--color-accent)] disabled:opacity-50"
        >
          {guardar.isPending ? t('common.saving') : t('designJourney.posterPresetSave')}
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-[10px] leading-snug text-[var(--color-fg-muted)]">
          {presets.isLoading ? t('common.loading') : t('designJourney.posterPresetsEmpty')}
        </p>
      ) : (
        <>
          {/* Elegir aplica, como en los estilos: es el gesto que la gente tiene aprendido. */}
          <Select
            value={String(elegido ?? '')}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              const preset = items.find((x) => x.id === id);
              if (preset) onAplicar(preset);
            }}
            className="h-8 text-[12px]"
            aria-label={t('designJourney.posterPresets')}
          >
            <option value="">{t('designJourney.posterPresetPick', { n: String(items.length) })}</option>
            {items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.canEdit ? '' : ` · ${t('designJourney.presetShared')}`}
              </option>
            ))}
          </Select>

          <div className="flex items-center gap-1.5">
            {seleccionado ? (
              <span className="flex flex-1 items-center gap-1 text-[11px] text-[var(--color-accent)]">
                <Check className="h-3 w-3" aria-hidden />
                {t('designJourney.presetApplied')}
                {modificado ? (
                  <span className="text-[var(--color-fg-muted)]"> · {t('designJourney.presetModified')}</span>
                ) : null}
              </span>
            ) : (
              <span className="flex-1" />
            )}

            <button
              type="button"
              disabled={!seleccionado}
              onClick={() => setAplicandoA(seleccionado)}
              className="flex items-center gap-1 rounded-md border border-[var(--color-border-default)] px-2 py-1 text-[11px] hover:border-[var(--color-accent)] disabled:opacity-40"
            >
              <Share2 className="h-3 w-3" aria-hidden />
              {t('designJourney.presetApplyElsewhere')}
            </button>

            {seleccionado?.canEdit ? (
              <button
                type="button"
                onClick={() => setARetirar(seleccionado)}
                aria-label={t('designJourney.posterPresetDelete')}
                className="shrink-0 rounded-md border border-[var(--color-border-default)] p-1 text-[var(--color-fg-muted)] hover:text-[var(--color-error)]"
              >
                <Trash2 className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>
        </>
      )}

      {error ? (
        <p role="alert" className="text-[10px] text-[var(--color-danger-fg)]">
          {error}
        </p>
      ) : null}

      <PromptDialog
        open={nombrando}
        onOpenChange={setNombrando}
        title={t('designJourney.posterPresetSave')}
        description={t('designJourney.posterPresetNameHint')}
        placeholder={t('designJourney.posterPresetNamePlaceholder')}
        submitLabel={t('common.save')}
        loading={guardar.isPending}
        onSubmit={(nombre) => guardar.mutate(nombre)}
      />

      <ApplyStyleDialog tipo="poster" preset={aplicandoA} onClose={() => setAplicandoA(null)} />

      <ConfirmDialog
        open={aRetirar != null}
        onOpenChange={(v) => !v && setARetirar(null)}
        tone="danger"
        title={t('designJourney.posterPresetDelete')}
        description={
          aRetirar ? t('designJourney.posterPresetDeleteConfirm', { name: aRetirar.name }) : ''
        }
        confirmLabel={t('designJourney.posterPresetDelete')}
        loading={retirar.isPending}
        onConfirm={() => aRetirar && retirar.mutate(aRetirar.id)}
      />
    </div>
  );
}
